import { Card, Typography, Button, FormControlLabel, Checkbox, Box, Alert, AlertTitle, Divider, Tooltip } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react'
import { Field, FormikProvider, useFormik } from 'formik';

import * as Yup from "yup";
import api from '../../../redux/features/auth/axiosAuthHelper';
import { TextEditField, TextEditReadOnlyField, StripTextEditField, StripSelectEditField, TwolineStripTextEditField, StripSelectReadOnlyEditField, StripTextReadOnlyEditField, TwolineStripReadOnlyTextEditField } from '../fields/fields';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import colorConfigs from '../../../configs/colorConfigs';
import { SkuSessionStorageKey, SkuTableParameterStorageService } from '../../../services/sessionStorage';
import RareButton from '../buttons/RareButton';
import { Sku, SkuStrip } from "../types";
import { drugCodes } from '../CustomHooks';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import StatusChip from '../StatusChip';

interface SkuDetailProps {
    sku_id?: any | null;
    handleRefreshButton: () => void;
    handleBackButton: () => void;
    onStatusChanged?: () => void;
}


const SkuDetailCard: React.FC<SkuDetailProps> = ({ sku_id, handleRefreshButton, handleBackButton, onStatusChanged }) => {
    const authData = useSelector((state: RootState) => state.auth);
    const [errors, setErrors] = useState<string | null>(null);
    const [sku, setSku] = useState<Sku>();
    const [activeStatus, setActiveStatus] = useState<boolean | null>(null);
    const [statusBusy, setStatusBusy] = useState(false);
    const [activeStrips, setActiveStrips] = useState<string[]>([]);

    const create = !sku_id;

    // Fetch sku data and point_model JSON data
    useEffect(() => {
        if (sku_id) {
            const fetchSku = async () => {
                try {
                    const url = `skus/${sku_id}`;

                    const response = await api.get(url, {
                        headers: {
                            'Authorization': `Bearer ${authData.access}`
                        }
                    });
                    setSku(response.data as Sku);
                    setActiveStatus(Boolean(response.data.is_active));
                } catch (error) {
                    console.error('Error fetching sku', error);
                    setErrors(`No Sku found with ID ${sku_id}`)
                    setTimeout(() => {
                        handleBackButton()
                    }, 4000);
                }
            };
            fetchSku();
        }
    }, [sku_id]);

    // Helper function: if sku data doesn’t include 10 strips, create default placeholders.
    // Update the return type to include a dragId property.
    const generateDefaultStrips = (existingStrips?: SkuStrip[]): (SkuStrip & { dragId: string })[] => {
        return new Array(10).fill(null).map((_, idx) => {
            const found = existingStrips?.find((s) => s.position === idx);
            if (found) {
                return { ...found, dragId: `strip-drag-id-${idx + 1}/10` };
            }
            return {
                id: 0,
                sku: 0,
                position: idx,
                label: '',
                threshold: 0,
                invalid_threshold: 0,
                dragId: `strip-drag-id-${idx + 1}/10`,
            };
        });
    };

    const extractErrorMessages = (data: any): string => {
        if (typeof data === 'string') {
            return data;
        }
        if (Array.isArray(data)) {
            return data.map(item => extractErrorMessages(item)).join(', ');
        }
        if (typeof data === 'object' && data !== null) {
            return Object.keys(data)
                .map(key => `${key}: ${extractErrorMessages(data[key])}`)
                .join('; ');
        }
        return String(data);
    };

    // Define validation schema for sku
    const validationSchema = Yup.object().shape({
        code: Yup.string().required('Code is required'),
        is_active: Yup.boolean().required('Status is required'),
        strips: Yup.array().of(
            Yup.object().shape({
                position: Yup.number().required(),
                label: Yup.string(),
                threshold: Yup.number().nullable().min(0, 'Value must be between 0 and 1.').max(1, 'Value must be between 0 and 1.'),
                invalid_threshold: Yup.number().nullable(),
            })
        )    // First test: there must be at least one non-empty label in the strips array.
            .test('min-one', 'At least one strip is required.', function (strips) {
                if (!strips) return false;
                const validStrips = strips
                    .map(strip => (strip.label || '').trim())
                    .filter(label => label !== '');
                return validStrips.length >= 1;
            })
            // Second test: each non-empty strip label must be unique.
            .test('unique-labels', 'Strip labels must be unique.', function (strips) {
                if (!strips) return true;
                const labels = strips
                    .map(strip => (strip.label || '').trim())
                    .filter(label => label !== '');
                const uniqueLabels = new Set(labels);
                return uniqueLabels.size === labels.length;
            }),
    });

    // Use a memoized version to avoid regenerating dragIds on each render.
    const initialStrips = useMemo(() => {
        return sku ? generateDefaultStrips(sku.strips) : generateDefaultStrips();
    }, [sku]);


    const formik = useFormik({
        enableReinitialize: true,
        validateOnChange: false,
        validateOnBlur: false,
        initialValues: {
            id: sku?.id || '',
            code: sku?.code || '',
            is_active: sku?.is_active ?? true,
            strips: initialStrips,
        },
        validationSchema,
        onSubmit: async (values) => {
            // Filter out any empty strip (assuming an empty label means the strip should be omitted).
            const submissionStrips = values.strips
                .filter((strip: SkuStrip & { dragId: string }) => strip.label.trim() !== '')
                .map((strip: SkuStrip & { dragId: string }) => ({
                    label: strip.label,
                    position: strip.position,
                    threshold: strip.threshold,
                    invalid_threshold: strip.invalid_threshold,
                }));

            // Build the data object for submission (only including code and the filtered strips)
            const data = sku
                ? { code: values.code, strips: submissionStrips }
                : { code: values.code, is_active: values.is_active, strips: submissionStrips };

            // Define URL and method for patch/post.
            const url = sku ? `skus/${sku.id}/` : `skus/`;
            const method = sku ? 'patch' : 'post';

            try {
                await api({
                    method,
                    url,
                    data,
                    headers: {
                        'Authorization': `Bearer ${authData.access}`,
                    },
                });
                setErrors(null);
                handleRefreshButton() // reset states and close popup
                const count = SkuTableParameterStorageService.get(SkuSessionStorageKey.count)
                SkuTableParameterStorageService.set(SkuSessionStorageKey.count, count + 1) // Updated count to not trigger refresh data button
            } catch (error: any) {
                if (error?.response?.data) {
                    const errorMessages = extractErrorMessages(error.response.data);
                    setErrors(errorMessages);
                } else {
                    setErrors("An unknown error occurred");
                }
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },
    });

    // Handle drag and drop reordering
    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        const newStrips = Array.from(formik.values.strips);
        const [removed] = newStrips.splice(result.source.index, 1);
        newStrips.splice(result.destination.index, 0, removed);
        newStrips.forEach((strip, index) => {
            strip.position = index; // adjust if you want 1-based: index + 1
        });
        formik.setFieldValue("strips", newStrips);
    };

    const changeStatus = async () => {
        if (!sku || activeStatus === null || statusBusy) return;
        const nextStatus = !activeStatus;
        setStatusBusy(true);
        setErrors(null);
        try {
            const response = await api({
                method: 'patch',
                url: `skus/${sku.id}/`,
                data: { is_active: nextStatus },
                headers: { Authorization: `Bearer ${authData.access}` },
            });
            if (response.data?.is_active !== nextStatus) throw new Error('Unexpected SKU status response');
            setActiveStatus(nextStatus);
            onStatusChanged?.();
        } catch (failure: any) {
            const detail = failure.response?.data?.is_active ?? failure.response?.data?.detail;
            setErrors(Array.isArray(detail) ? detail.join(' ') :
                typeof detail === 'string' ? detail : 'The SKU status could not be changed. Please try again.');
        } finally {
            setStatusBusy(false);
        }
    };

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
                <Box data-testid="Box-5899" sx={{ width: '100%', minWidth: 0, px: { xs: 1, sm: 2 }, pt: 1, pb: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {errors && (
                        <Alert data-testid="Alert-t5wb" severity="error" style={{ width: "100%" }}>
                            <AlertTitle data-testid="AlertTitle-288k">Error</AlertTitle>
                            {errors}
                        </Alert>
                    )}
                    {formik.errors.strips && typeof formik.errors.strips === 'string' && (
                        <Alert data-testid="Alert-fu4i" severity="error" style={{ width: "100%" }}>
                            <AlertTitle data-testid="AlertTitle-ur9k">Error</AlertTitle>
                            {formik.errors.strips}
                        </Alert>
                    )}
                    <Box sx={{ maxWidth: 520, ...(!create ? { '& input': { color: colorConfigs.brand.logoText } } : {}) }}>
                        <Field data-testid="Field-9awx" name="code" type="text" className="form-control" label="Sku Code" component={create ? TextEditField : TextEditReadOnlyField} />
                    </Box>
                    <Box sx={{ borderTop: '1px solid #e1e8e6', pt: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ color: colorConfigs.sidebar.color }}>Strip configuration</Typography>
                        {create && <Typography variant="caption" sx={{ color: '#58646a' }}>Add strips below, then drag to arrange their order.</Typography>}
                    </Box>
                    <Box sx={{ overflowX: 'auto', minWidth: 0, pb: 1 }}>
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="strips" direction="horizontal">
                                {(provided) => (
                                    <Box data-testid="Box-tp3v"
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        sx={{ display: "flex", gap: 1, width: "max-content", minWidth: "100%" }}
                                    >
                                        {formik.values.strips.map((strip: SkuStrip & { dragId: string }, index: number) => {
                                            // Determine whether we are in "editing" mode if the plus card is clicked,
                                            // or if a non-empty label is already present.
                                            const isActive = activeStrips.includes(strip.dragId) || strip.label.trim() !== "";
                                            return (
                                                <Draggable key={strip.dragId} draggableId={strip.dragId} index={index} isDragDisabled={create ? false : true}>
                                                    {(provided) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                        >
                                                            <Card data-testid={`Card-qbrz-${index}`} sx={{ width: { xs: 130, sm: 120, md: 120 }, minHeight: 260, padding: 1, boxShadow: 0, border: '1px solid #e1e8e6', borderRadius: 2, flexShrink: 0 }}>
                                                                {/* Drag handle area */}
                                                                <Box data-testid={`Box-${index}`}
                                                                    {...provided.dragHandleProps}  // Drag handle is applied here.
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        cursor: create ? 'grab' : 'default',
                                                                        mb: 1,
                                                                    }}
                                                                >
                                                                    <Typography variant="body2" fontWeight={700} sx={{ color: colorConfigs.brand.logoText }}>{index + 1}</Typography>
                                                                    {create ? <DragIndicatorIcon data-testid={`Drag-icon-${strip.dragId}`} sx={{ transform: 'rotate(90deg)' }} /> : <LockOutlinedIcon fontSize="small" />}
                                                                    {create && strip.label.trim() !== '' && (
                                                                        <Tooltip title="Remove strip">
                                                                            <IconButton aria-label={`Remove strip ${index + 1}`} size="small" onClick={() => {
                                                                                formik.setFieldValue(`strips[${index}].label`, '');
                                                                                setActiveStrips(prev => prev.filter(id => id !== strip.dragId));
                                                                            }}>
                                                                                <CloseIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </Box>
                                                                <Divider sx={{ borderColor: '#e1e8e6', mb: 2 }} />
                                                                {isActive ? (
                                                                    <Box data-testid={`Fields-container-${strip.dragId}`} sx={{ display: 'flex', flexDirection: 'column', gap: 4.5 }}>
                                                                        <Field data-testid={`Field-${strip.dragId}-label`}
                                                                            testid={`Field-${strip.dragId}`}
                                                                            name={`strips[${index}].label`}
                                                                            component={create ? StripSelectEditField : StripSelectReadOnlyEditField}
                                                                            options={drugCodes}
                                                                            label="Label"
                                                                            // Optional: onBlur, if label remains empty, remove from active list:
                                                                            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                                                                                if (e.target.value.trim() === "") {
                                                                                    setActiveStrips((prev) => prev.filter((i) => i !== strip.dragId));
                                                                                }
                                                                            }}
                                                                        />
                                                                        <Field data-testid={`Field-${strip.dragId}-threshold`}
                                                                            name={`strips[${index}].threshold`}
                                                                            component={create ? StripTextEditField : StripTextReadOnlyEditField}
                                                                            label="Threshold"
                                                                            type="number"
                                                                        />
                                                                        <Field data-testid={`Field-${strip.dragId}-invalid_threshold`}
                                                                            name={`strips[${index}].invalid_threshold`}
                                                                            component={create ? TwolineStripTextEditField : TwolineStripReadOnlyTextEditField}
                                                                            label={<>Invalid<br />Threshold</>}
                                                                            type="number"
                                                                        />
                                                                    </Box>
                                                                ) : (
                                                                    // Render a card with a plus icon that, when clicked, marks this index active.
                                                                    <Box data-testid={`Box-${strip.dragId}`}
                                                                        sx={{
                                                                            width: "100%",
                                                                            height: "100%",
                                                                            display: "flex",
                                                                            justifyContent: "center",
                                                                            alignItems: "center",
                                                                        }}
                                                                    >
                                                                        {create &&
                                                                            <IconButton data-testid={`PlusButton-${strip.dragId}`}
                                                                                onClick={() =>
                                                                                    setActiveStrips((prev) =>
                                                                                        prev.includes(strip.dragId) ? prev : [...prev, strip.dragId]
                                                                                    )
                                                                                }
                                                                            >
                                                                                <AddIcon data-testid={`PlusButton-${strip.dragId}`} sx={{ fontSize: 40 }} />
                                                                            </IconButton>
                                                                        }
                                                                    </Box>
                                                                )}
                                                            </Card>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </Box>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        {create ? (
                            <FormControlLabel
                                sx={{ m: 0 }}
                                control={<Checkbox checked={formik.values.is_active} name="is_active" onChange={formik.handleChange} sx={{ '&.Mui-checked': { color: colorConfigs.buttons.active } }} />}
                                label={<StatusChip enabled={formik.values.is_active} trueLabel="Active" falseLabel="Inactive" />}
                            />
                        ) : activeStatus !== null ? (
                            <StatusChip enabled={activeStatus} trueLabel="Active" falseLabel="Inactive" />
                        ) : null}
                    </Box>
                    <Box data-testid="div-rd25" className="form-group" sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, borderTop: '1px solid #e1e8e6', pt: 1.5 }}>
                        {!create && sku && authData.isPortalUser && (
                            <Button type="button" disabled={statusBusy || formik.isSubmitting || activeStatus === null}
                                onClick={() => void changeStatus()}
                                sx={{ mr: 'auto', textTransform: 'none', fontWeight: 700,
                                    color: activeStatus ? '#b3261e' : colorConfigs.buttons.active,
                                    backgroundColor: activeStatus ? '#fcf4f3' : '#f1f8f2',
                                    '&:hover': { backgroundColor: activeStatus ? '#fbe9e7' : '#e5f3e8' },
                                }}>
                                {statusBusy ? 'Updating status…' : activeStatus ? 'Deactivate SKU' : 'Reactivate SKU'}
                            </Button>
                        )}
                        <RareButton data-testid="RareButton-b320"
                            onClick={() => handleBackButton()}
                            variant="outlined"
                            sx={{
                                position: "relative",
                                m: 1,
                            }}
                        >
                            {create ? 'Cancel' : 'Close'}
                        </RareButton>
                        {create && <RareButton data-testid="RareButton-vj93"
                            type="submit"
                            variant="success"
                            disabled={statusBusy || formik.isSubmitting}
                            sx={{
                                position: "relative",
                                m: 1,
                            }}
                        >
                            Create SKU
                        </RareButton>}
                    </Box>
                </Box>
            </form>
        </FormikProvider>
    )
}

export default SkuDetailCard;
